<template>
  <v-data-table
    :headers="headers"
    :items="computedProfiles"
    class="pa-6"
    :search="search"
    :custom-filter="customFilter"
  >
    <template v-slot:top>
      <profile-filters />
      <v-text-field
        v-model="search"
        label="Search"
        style="max-width: 400px"
        class="py-4"
        single-line
        outlined
        dense
        :hide-details="true"
        append-icon="mdi-magnify"
      />
    </template>

    <template v-slot:body="{ items }">
      <tr
        v-for="item in items"
        :key="`profile-for-notification-${item.id}`"
        class="cursor-pointer profile-table-row"
        :class="{selected: profile && item.id === profile.id}"
        @click="setProfile(item)"
      >
        <td>
          {{ item.name }}
        </td>
        <td>
          {{ item.latestVersion }}
        </td>
        <td>
          <v-icon v-if="item.released && !item.deleted" color="green"
            >mdi-check</v-icon
          >
          <v-icon v-else-if="!item.deleted" color="red">mdi-close</v-icon>
        </td>
        <td>
          <v-icon v-if="item.deleted" color="green">mdi-check</v-icon>
          <v-icon v-else color="red">mdi-close</v-icon>
        </td>
        <td>
          <span v-if="!item.deleted && !item.expires">
            Never
          </span>
          <span v-else-if="!item.deleted && item.expired">
            Expired
          </span>
          <span v-else-if="!item.deleted">
            {{ format(item.expires, "d LLLL Y") }}
          </span>
        </td>
        <td>
          <span v-if="item.potentialReach >= 0">
            {{ item.potentialReach }}
          </span>
        </td>
        <td class="text-align-right d-flex py-1">
          <release-profile-modal
            v-if="canRelease(item)"
            :profile="item"
          />

          <v-tooltip bottom>
            <template v-slot:activator="tooltip">
              <v-btn
                v-if="canEdit(item)"
                class="cvc-button mr-2"
                @click="() => editProfile(item)"
              >
                <div v-bind="tooltip.attrs" v-on="tooltip.on">
                  <v-icon>mdi-pencil</v-icon>
                </div>
              </v-btn>
            </template>
            <span>Edit</span>
          </v-tooltip>

          <delete-profile-modal v-if="canDelete(item)" :profile="item" />
        </td>
      </tr>
    </template>
  </v-data-table>
</template>

<script>
import ProfileFilters from "@/components/ProfileTags/ProfileFilters";
import ReleaseProfileModal from "@/components/ProfileTags/ReleaseProfileModal";
import DeleteProfileModal from "@/components/ProfileTags/DeleteProfileModal";
import { format, isAfter, parse, parseISO, startOfToday } from "date-fns";
import { toTitleCase } from "@/helpers/TextUtils";

export default {
  name: "ProfileTable",
  components: {
    ProfileFilters,
    ReleaseProfileModal,
    DeleteProfileModal,
  },
  props: {
    profile: {
      required: true,
    },
    setProfile: {
      required: true,
      type: Function,
    },
  },
  data() {
    return {
      parse,
      format,
      search: "",
      headers: [
        {
          text: "Name",
          value: "name",
          align: "left",
          sortable: true,
          filterable: true,
        },
        {
          text: "Latest Version",
          value: "latestVersion",
          align: "left",
          sortable: false,
          filterable: false,
        },
        {
          text: "Released",
          value: "released",
          align: "left",
          sortable: false,
          filterable: false,
        },
        {
          text: "Deleted",
          value: "deleted",
          align: "left",
          sortable: false,
          filterable: false,
        },
        {
          text: "Expires",
          value: "expires",
          align: "left",
          sortable: true,
          filterable: false,
          sort(a, b) {
            if (a === null && b === null) return 0;
            if (a === null) return 1;
            if (b === null) return -1;
            return isAfter(a, b) ? 1 : -1;
          },
        },
        {
          text: "Potential Reach",
          value: "potentialReach",
          align: "left",
          sortable: true,
          filterable: false,
        },
        {
          text: "",
          value: "edit",
          align: "right",
          sortable: false,
          filterable: false,
        },
      ],
    };
  },
  computed: {
    profileSizes() {
      const profileSizesByWeek = this.$store.state.profile.profileSizesByWeek;
      if (!profileSizesByWeek) return {};
      return profileSizesByWeek[profileSizesByWeek.length - 1];
    },
    flagFilters() {
      return this.$store.state.profileReview.flagFilters;
    },
    computedProfiles() {
      if (this.$store.getters.profileDefinitionsLoading) return [];
      const filterDeleted = this.flagFilters.deleted;
      const filterReleased = this.flagFilters.released;
      const filterExpired = this.flagFilters.expired;
      const today = startOfToday();

      return this.$store.state.profile.profileDefinitionsData
        .map((v) => ({
          id: v.metadata.name,
          name:
            v.metadata.annotations &&
            v.metadata.annotations["insights/display-name"]
              ? v.metadata.annotations["insights/display-name"]
              : toTitleCase(v.metadata.name),
          description:
              (v.metadata.annotations && v.metadata.annotations["insights/description"]) || "N/A",
          released: v.metadata.flags && v.metadata.flags.released,
          deleted: v.metadata.flags && v.metadata.flags.deleted,
          latestVersion: v.metadata.profile_version,
          expires: v.metadata.expires ? parseISO(v.metadata.expires) : null,
          potentialReach:
            this.profileSizes[v.metadata.name] !== undefined
              ? this.profileSizes[v.metadata.name]
              : -1,
          query: v.query,
        }))
        .map((item) => ({
          expired: item.expires !== null && isAfter(today, item.expires),
          ...item,
        }))
        .filter(
          (item) =>
            ((filterDeleted !== "Yes" && !item.deleted) ||
              (filterDeleted === "Yes" && item.deleted)) &&
            ((filterReleased !== "Yes" && !item.released) ||
              (filterReleased !== "No" && item.released)) &&
            ((filterExpired !== "Yes" && !item.expired) ||
              (filterExpired === "Yes" && item.expired))
        );
    },
  },
  methods: {
    customFilter(value, filters) {
      return (
        value !== null &&
        filters !== null &&
        value
          .toString()
          .toLowerCase()
          .indexOf(filters.toLowerCase()) !== -1
      );
    },
    canRelease(item) {
      return !item.deleted && !item.released && !item.expired;
    },
    canEdit(item) {
      return (
        this.$store.state.login.can_add_profiles &&
        this.$store.state.login.can_change_profiles &&
        !item.deleted
      );
    },
    canDelete(item) {
      return this.$store.state.login.can_delete_profiles && !item.deleted;
    },
    editProfile(item) {
      this.$store
        .dispatch("setSelectedProfile", {
          id: item.id,
        })
        .then(() => {
          this.$router.push({ name: "NotificationsSegments", query: { edit: true } });
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-table-row {
  height: 52px;
  td {
    padding: 0 16px;
  }
  &:hover {
    background-color: #A9B4F9 !important;
  }

  // Apparently, asking for a border on a row is too much to ask for vuetify.
  // So add it to the tds instead of the tr.
  &.selected {
    td:first-child {
      border-left: #A9B4F9 1px solid !important;
    }
    td {
      border-top: #A9B4F9 1px solid !important;
      border-bottom: #A9B4F9 1px solid !important;
    }
    td:last-child {
      border-right: #A9B4F9 1px solid !important;
    }
  }
}
</style>
